import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BellOff, BellRing, AtSign, Check, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";

const LEVELS = [
  { value: "all", label: "All activity", hint: "Everything this group notifies about today", Icon: BellRing },
  { value: "important", label: "Important only", hint: "Announcements and mentions", Icon: Bell },
  { value: "mentions", label: "Mentions only", hint: "Only when someone @mentions you", Icon: AtSign },
  { value: "muted", label: "Muted", hint: "No activity notifications from this group", Icon: BellOff },
];

const ICON_FOR = { all: BellRing, important: Bell, mentions: AtSign, muted: BellOff };

/** A member's own notification settings for ONE group. Private to them — no organizer
 *  surface exists for it. Reading never creates a row, so the defaults just show through. */
export function GroupNotificationMenu({ groupId }) {
  const qc = useQueryClient();
  const prefs = useQuery({
    queryKey: ["group-notif-prefs", groupId],
    queryFn: async () => (await api.get(`/groups/${groupId}/notification-prefs`)).data,
    retry: false,
  });
  const save = useMutation({
    mutationFn: async (body) => (await api.put(`/groups/${groupId}/notification-prefs`, body)).data,
    onSuccess: (data) => {
      qc.setQueryData(["group-notif-prefs", groupId], data);
      qc.invalidateQueries({ queryKey: ["groups-mine"] });
      toast.success("Notification settings saved.");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "That didn't save."),
  });

  const level = prefs.data?.level || "all";
  const emailOn = prefs.data?.email === "important";
  const Current = ICON_FOR[level] || Bell;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" title="Notification settings"
                aria-label="Notification settings for this group"
                data-testid="group-notif-trigger">
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" />
            : <Current className="h-4 w-4" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72" data-testid="group-notif-menu">
        <DropdownMenuLabel>Notify me about</DropdownMenuLabel>
        {LEVELS.map(({ value, label, hint, Icon }) => (
          <DropdownMenuItem key={value} className="gap-2.5 py-2"
                            data-testid={`group-notif-level-${value}`}
                            onClick={() => save.mutate({ level: value })}>
            <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="flex-1">
              <span className="block text-sm text-foreground">{label}</span>
              <span className="block text-xs text-muted-foreground">{hint}</span>
            </span>
            {level === value ? <Check className="h-4 w-4 text-primary" /> : null}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem className="gap-2.5 py-2" data-testid="group-notif-email-toggle"
                          disabled={level === "muted"}
                          onSelect={(e) => {
                            e.preventDefault();
                            save.mutate({ email: emailOn ? "off" : "important" });
                          }}>
          <Mail className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="flex-1">
            <span className="block text-sm text-foreground">Email me important updates</span>
            <span className="block text-xs text-muted-foreground">
              {level === "muted" ? "Unavailable while this group is muted"
                : "Announcements only, never every post"}
            </span>
          </span>
          {emailOn ? <Check className="h-4 w-4 text-primary"
                           data-testid="group-notif-email-on" /> : null}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
